import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import SafetyLoopLogo from '../../images/SL_logo1.png';
import { ReactComponent as Logo } from '../../fonts/logo.svg';

// SafetyLoop -> ProjectLoop mark pair used by the SSO landing page and the
// SafetyLoop import wizard. `animated` sends dots across the track and pulses
// the ProjectLoop tile, so the bridge reads as "working".
const travel = keyframes`
  0% { transform: translateX(0); background: #5db4e5; opacity: 0; }
  15% { opacity: 1; }
  85% { opacity: 1; }
  100% { transform: translateX(var(--travel)); background: #fc8543; opacity: 0; }
`;

const ring = keyframes`
  0% { transform: scale(0.92); opacity: 0.55; }
  100% { transform: scale(1.3); opacity: 0; }
`;

const Marks = styled.div`
  --mark: ${({ $size }) => $size}px;
  --dot: ${({ $size }) => Math.max(4, Math.round($size / 9))}px;
  --track: ${({ $size }) => Math.round($size * 1.3)}px;
  --travel: calc(var(--track) - var(--dot));
  display: inline-flex;
  align-items: center;
  gap: ${({ $size }) => Math.round($size / 5)}px;

  .mark {
    position: relative;
    display: grid;
    place-items: center;
    width: var(--mark);
    height: var(--mark);
    border-radius: calc(var(--mark) * 0.24);
    flex: none;
  }

  /* SL_logo1.png is a 143px tile at +78+70 on a transparent 299x298 canvas:
     scale the canvas so the tile fills the mark, then centre the tile. */
  .mark-sl img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(var(--mark) * 2.09);
    max-width: none;
    transform: translate(-50%, -47.5%);
    pointer-events: none;
  }

  .mark-pl {
    background: #fff3ec;
    border: 2px solid #fc8543;
    svg {
      width: 62%;
      height: auto;
    }
  }

  .track {
    position: relative;
    width: var(--track);
    height: var(--dot);
    i {
      position: absolute;
      top: 0;
      width: var(--dot);
      height: var(--dot);
      border-radius: 50%;
      background: #d3d3d3;
    }
    i:nth-child(1) {
      left: 15%;
    }
    i:nth-child(2) {
      left: calc(50% - var(--dot) / 2);
      background: #b9c9d6;
    }
    i:nth-child(3) {
      right: 15%;
      background: #fcb48c;
    }
  }

  ${({ $animated }) =>
    $animated &&
    css`
      .track i {
        left: 0;
        right: auto;
        animation: ${travel} 1.5s cubic-bezier(0.45, 0, 0.55, 1) infinite;
      }
      .track i:nth-child(1) {
        left: 0;
      }
      .track i:nth-child(2) {
        left: 0;
        animation-delay: 0.5s;
      }
      .track i:nth-child(3) {
        left: 0;
        right: auto;
        animation-delay: 1s;
      }

      .mark-pl::after {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: calc(var(--mark) * 0.3);
        border: 2px solid #fc8543;
        animation: ${ring} 1.8s ease-out infinite;
      }

      @media (prefers-reduced-motion: reduce) {
        .track i,
        .mark-pl::after {
          animation: none;
        }
        .track i:nth-child(1) {
          left: 15%;
          opacity: 1;
        }
        .track i:nth-child(2) {
          left: calc(50% - var(--dot) / 2);
          opacity: 1;
        }
        .track i:nth-child(3) {
          left: auto;
          right: 15%;
          opacity: 1;
        }
      }
    `}
`;

const BridgeMarks = ({ size = 56, animated = false }) => (
  <Marks $size={size} $animated={animated} aria-hidden="true">
    <span className="mark mark-sl">
      <img src={SafetyLoopLogo} alt="" />
    </span>
    <span className="track">
      <i />
      <i />
      <i />
    </span>
    <span className="mark mark-pl">
      <Logo />
    </span>
  </Marks>
);

export default BridgeMarks;
